import PropTypes from 'prop-types';
import React from 'react';
import { FormattedDate, FormattedMessage } from 'react-intl';
import moment from 'moment';

import { Error as ErrorIcon, Info as InfoIcon, Warning as WarningIcon } from '@bibliocommons/base-icons';

import ShortFormattedDate from 'app/components/shared/ShortFormattedDate';
import { CARD_EXPIRY_STATES } from 'app/helpers/borrowing/cardExpiry';

import './AccountExpiryWarning.scss';

// state => [modifier, icon, message id]. State is carried by icon, colour and wording together, so
// it survives greyscale, low vision and colour blindness.
const ALERT = {
  [CARD_EXPIRY_STATES.FIRST_REMINDER]: ['first-reminder', InfoIcon, 'label_renew_card_by'],
  [CARD_EXPIRY_STATES.SECOND_REMINDER]: ['second-reminder', WarningIcon, 'label_renew_card_by'],
  [CARD_EXPIRY_STATES.EXPIRED]: ['expired', ErrorIcon, 'label_card_expired_on']
};

export default function AccountExpiryWarning({ expiryDate, cardExpiryState }) {
  // The expiryDate guard keeps origin/develop's `expiryDate &&` short-circuit. Without it an alert
  // state with no date renders moment.utc(undefined), i.e. today, as the expiry date. Unreachable
  // through resolveCardExpiryState, which returns null without a date -- but the component should
  // not depend on its caller for that.
  if (!cardExpiryState || !expiryDate) return null;

  // CARD_EXPIRY_BANNER_REDESIGN off: what every borrower sees today, unchanged.
  if (cardExpiryState === CARD_EXPIRY_STATES.LEGACY) {
    return (
      <a className="cp-account-expiry-warning list-group-item" href="/account">
        <FormattedMessage id="label_renew_card_by" /> <ShortFormattedDate value={expiryDate} />
      </a>
    );
  }

  // Degrade to no banner rather than throwing if a state ever arrives without an ALERT row. No
  // test: propTypes rejects such a value, and the harness turns that warning into a throw.
  if (!ALERT[cardExpiryState]) return null;

  const [modifier, Icon, messageId] = ALERT[cardExpiryState];

  // No renewal call to action: the row is already an anchor, so a link inside it would be a nested
  // anchor -- invalid markup, and a keyboard and screen-reader trap.
  return (
    <a className="cp-account-expiry-warning cp-account-expiry-warning--alert list-group-item" href="/account">
      <span className={`expiry-alert expiry-alert--${modifier}`}>
        <span className="expiry-alert__icon">
          <Icon />
        </span>
        <span className="expiry-alert__content">
          {/* The span is load-bearing: two adjacent text nodes would merge into one anonymous
              flex item and share a line. Neither message id carries a placeholder, so no `values`. */}
          <span>
            <FormattedMessage id={messageId} />
          </span>
          {/* Long form, matching v1's '%B %e, %Y'. ShortFormattedDate is shared with holds and
              checkouts, so the format is applied here rather than there. */}
          <FormattedDate value={moment.utc(expiryDate).toDate()} year="numeric" month="long" day="numeric" />
        </span>
      </span>
    </a>
  );
}

AccountExpiryWarning.propTypes = {
  expiryDate: PropTypes.string,
  cardExpiryState: PropTypes.oneOf(Object.values(CARD_EXPIRY_STATES))
};
